import i18n from '@/i18n'
import { moduleLabel } from '@/lib/module-label'
import { getTerminologyMode } from '@/lib/useTerminology'

/**
 * Übersetzt bekannte Backend-Fehler-Keys (das Backend wirft rohe i18n-Keys wie
 * `monthlyReport.monthLocked`) in verständlichen Klartext — NUR für die Anzeige.
 *
 * Der rohe Key bleibt in `err.response.data.message` erhalten; an Stellen, die den
 * Key für Steuerungslogik prüfen, NICHT übersetzen, nur beim Setzen einer für den
 * Nutzer sichtbaren Fehlermeldung.
 */
const API_ERROR_MESSAGES: Record<string, string> = {
  // AP-20: Monats-Sperre nach verbindlicher Abgabe des Monatsberichts
  'monthlyReport.monthLocked':
    'Der Monatsbericht für diesen Monat wurde bereits verbindlich abgegeben. ' +
    'Buchungen und Änderungen sind gesperrt, bis die Abgabe widerrufen wird.',
  // AP-20: Re-Authentifizierung bei der Abgabe (FES)
  'monthlyReport.invalidPin': 'PIN ist nicht korrekt.',
  'monthlyReport.invalidPassword': 'Passwort ist nicht korrekt.',
  'monthlyReport.pinNotSet': 'Für dein Konto ist keine PIN hinterlegt — bitte mit Passwort bestätigen.',
  'monthlyReport.credentialRequired': 'Bitte PIN oder Passwort eingeben.',
  'monthlyReport.alreadySubmitted': 'Dieser Monat wurde bereits abgegeben.',

  // AP-36: Stand-Prüfung. Der Baustein in `lib/speichern/` löst 409 selbst auf;
  // diese Texte erscheinen nur, wo eine Maske den Fehler ohne ihn zeigt.
  'common.staleState': 'Der Datensatz wurde inzwischen von jemand anderem geändert. Bitte neu laden.',
  'common.staleStateInvalid': 'Der Stand des Datensatzes ist ungültig. Bitte neu laden.',
  'errors.concurrentUpdate': 'Gleichzeitige Änderung — bitte noch einmal versuchen.',
  // AP-45: Ein Verweis (Projekt, Position, Phase …) zeigt auf nichts, was es bei diesem Mandanten gibt.
  'errors.referenceUnknown': 'Ein ausgewählter Eintrag wurde nicht gefunden. Bitte die Auswahl prüfen und neu wählen.',

  // Mandanten-Einstellungen
  'tenants.primaryColorInvalid':
    'Bitte eine Farbe als 6-stelligen Hex-Wert angeben, z. B. #0ea5e9.',

  // Zeiterfassung
  'timeEntries.notFound': 'Zeiteintrag nicht gefunden.',
  'timeEntries.alreadyRunning': 'Es läuft bereits eine Stempeluhr.',
  'timeEntries.noRunningEntry': 'Keine laufende Stempeluhr gefunden.',
  'timeEntries.cannotEditSynced': 'Synchronisierte Einträge können nicht bearbeitet werden.',
  'timeEntries.cannotDeleteSyncedOrReviewed':
    'Synchronisierte oder geprüfte Einträge können nicht gelöscht werden.',
  'timeEntries.editWindowExpired': 'Das Bearbeitungsfenster ist abgelaufen.',
  'timeEntries.editNotAllowed': 'Bearbeitung ist nicht erlaubt.',
  'timeEntries.editNoteRequired': 'Bitte gib eine Begründung für die Änderung an.',
  'timeEntries.deleteNotAllowed': 'Löschen ist nicht erlaubt.',
  'timeEntries.invalidTimeRange': 'Die Endzeit muss nach der Startzeit liegen.',
  'timeEntries.invalidDateTimeFormat': 'Ungültiges Datums- oder Zeitformat.',
  'timeEntries.userNotFound': 'Der ausgewählte Mitarbeiter wurde nicht gefunden.',
  // KA-35: Im ERP gibt es keinen Scanner — die Meldung sagt, wo es weitergeht.
  'timeEntries.qrTokenRequired': 'Für dich gilt die QR-Pflicht. Stempel bitte in der Mitarbeiter-App mit dem QR-Code — im ERP ohne QR-Code stempelt nur, wer das Recht „QR-Pflicht: im ERP ausgenommen“ hat.',

  // Fehlzeiten
  'absences.notFound': 'Fehlzeit nicht gefunden.',
  'absences.forbidden': 'Keine Berechtigung für diese Fehlzeit.',
  'absences.typeNotFound': 'Fehlzeit-Typ nicht gefunden oder inaktiv.',
  'absences.userNotFound': 'Mitarbeiter nicht gefunden.',

  // Diäten / Tagesdaten
  'dailyRecords.cannotEditSubmitted': 'Eingereichte Tagessätze können nicht bearbeitet werden.',
  'dailyRecords.alreadySubmitted': 'Dieser Tagessatz wurde bereits eingereicht.',
  'dailyRecords.userNotFound': 'Der ausgewählte Mitarbeiter wurde nicht gefunden.',

  // AP-35: Projektmanagement (Detailblatt 1 7., Blatt 3 11., Blatt 13 6.)
  'projects.errors.notFound': 'Dieser Datensatz gibt es nicht mehr.',
  'projects.errors.clientRequired': 'Für einen Auftrag muss ein Kunde gewählt sein.',
  'projects.errors.clientNotCustomer': 'Der gewählte Geschäftspartner ist kein aktiver Kunde.',
  'projects.errors.contactMismatch': 'Der Ansprechpartner gehört nicht zum gewählten Kunden.',
  'projects.errors.addressAmbiguous': 'Adresse entweder aus dem Partner wählen oder frei eingeben — nicht beides.',
  'projects.errors.siteRequired': 'Bitte eine Betriebsstätte wählen.',
  'projects.errors.templateType': 'Nur interne Vorhaben können Vorlagen sein.',
  'projects.errors.numberTaken':
    'Diese Nummer ist bereits vergeben — auch ein Auftrag der Zeiterfassung zählt dazu.',
  'projects.errors.numberManualDisabled':
    'Nummern werden automatisch vergeben. Das Eintippen erlaubst du in den Einstellungen.',
  'projects.errors.numberExhausted': 'Es ließ sich keine freie Nummer finden. Bitte den Nummernkreis prüfen.',
  'projects.errors.initialStatusInvalid': 'Mit diesem Status kann nichts angelegt werden.',
  'projects.errors.statusTransition': 'Dieser Statuswechsel ist nicht möglich.',
  'projects.errors.statusReasonRequired': 'Bitte eine Begründung angeben.',
  'projects.errors.statusDerived': 'Der Status eines Sammelauftrags folgt seinen Positionen.',
  'projects.errors.billingNotDone': 'Die Abrechnung ist noch nicht als fertig freigegeben.',
  'projects.errors.readonly': 'In diesem Status lässt sich nichts mehr ändern.',
  'projects.errors.readonlyInvoiced':
    'Abgerechnet: Es lassen sich nur noch Anmerkungen, Gewährleistung und Haftrücklass ändern.',
  'projects.errors.clientChangeWarn': 'Festgeschriebene Belege tragen noch den alten Kunden.',
  'projects.errors.currencyLocked': 'Die Währung lässt sich nicht mehr ändern.',
  'projects.errors.typeLockedInvoiced': 'Der Typ lässt sich nicht ändern: Es ist schon abgerechnet.',
  'projects.errors.typeLockedDocuments': 'Der Typ lässt sich nicht ändern: Es hängen Belege daran.',
  'projects.errors.phaseNotFound': 'Diese Phase gibt es nicht mehr.',
  'projects.errors.phaseLastActive': 'Mindestens eine Phase muss aktiv bleiben.',
  'projects.errors.phaseLastAssembly': 'Genau eine Phase ist die Montage — zuerst eine andere dazu erklären.',
  'projects.errors.phaseInUse': 'Diese Phase wird verwendet — deaktivieren statt löschen.',
  'projects.errors.phaseSlotRange': 'Der Beginn liegt nach dem Ende.',
  'projects.errors.phaseSlotTimeWithoutFixed': 'Eine Uhrzeit gibt es nur bei einem fixierten Termin.',
  'projects.errors.positionNotFound': 'Diese Position gibt es nicht mehr.',
  'projects.errors.contactNotFound': 'Diesen Ansprechpartner gibt es nicht.',
  'projects.errors.participantNameRequired': 'Ein Beteiligter braucht einen Namen — oder wähle ihn aus den Geschäftspartnern.',
  'projects.errors.billingIntervalMismatch': 'Das Intervall passt nicht zum Abrechnungsschema.',
  'projects.errors.participantContactInactive': 'Die Person gehört zu einem inaktiven Geschäftspartner.',
  'projects.errors.participantDuplicate': 'Diese Person ist bereits Teilnehmer.',
  'projects.errors.participantNotFound': 'Diesen Teilnehmer gibt es nicht mehr.',
  'projects.errors.qualificationNotFound': 'Diese Qualifikation gibt es nicht mehr.',
  'projects.errors.qualificationInUse': 'Die Qualifikation wird verwendet — deaktivieren statt löschen.',
  'projects.errors.regieRateDuplicate': 'Für diese Qualifikation gibt es schon einen Satz ab diesem Datum.',
  'projects.errors.regieRateArticleKind': 'Der gewählte Artikel ist kein Leistungsartikel.',
  'projects.errors.regieRateNotFound': 'Diesen Satz gibt es nicht mehr.',
  'projects.errors.paymentPlanSum': 'Die Raten überschreiten 100 %.',
  'projects.errors.paymentPlanItemBilled': 'Diese Rate ist bereits abgerechnet.',
  'projects.errors.approvalNotFound': 'Diese Freigabe gibt es nicht.',
  'projects.errors.journalTypeNotFound': 'Diesen Journal-Typ gibt es nicht mehr.',
  'projects.errors.journalTypeSystem': 'Ein System-Typ lässt sich umbenennen, aber nicht abschalten.',
  'projects.errors.templateNotFound': 'Diese Vorlage gibt es nicht mehr.',
  'projects.errors.settingTabUnknown': 'Diesen Einstellungsbereich gibt es nicht.',
  'projects.errors.settingTabNotBooked': 'Dieser Bereich gehört zu einem Baustein, der nicht gebucht ist.',
  'projects.errors.settingFieldWrongTab': 'Dieses Feld gehört zu einem anderen Bereich der Einstellungen.',
  'projects.errors.settingFieldNotBooked': 'Dieses Feld gehört zu einem Baustein, der nicht gebucht ist.',
  'projects.errors.sequencePatternInvalid': 'Das Muster braucht eine Zählstelle ({nummer}).',
  'projects.errors.sequenceDecrease': 'Der Zähler kann nicht unter den letzten vergebenen Wert gesetzt werden.',
  'projects.errors.thresholdOrder': 'Die Warnschwelle muss vor der Alarmschwelle liegen.',

  // AP-35 Phase D — Positionen (Blatt 2 11.), Arbeitsschritte (Blatt 4 10.),
  // Journal und Aufgaben (Blatt 9 8.), Dateien (Blatt 6 6.).
  'projects.errors.positionQuantity':
    'Die Menge muss größer als 0 sein und zu den Nachkommastellen der Einheit passen.',
  'projects.errors.positionUnitInactive': 'Diese Einheit ist nicht mehr aktiv.',
  'projects.errors.positionArticleNotSales': 'Dieser Artikel ist kein Verkaufsartikel.',
  'projects.errors.positionNumberTaken': 'Diese Positionsnummer gibt es hier schon.',
  'projects.errors.positionNumberLocked':
    'Die Nummer steht schon auf einem Beleg oder trägt gebuchte Stunden — sie bleibt.',
  'projects.errors.positionRenumberBlocked':
    'Neu nummerieren geht nur, solange keine Position auf einem Beleg steht oder Stunden trägt.',
  'projects.errors.positionStatusTransition': 'Dieser Statuswechsel ist nicht vorgesehen.',
  'projects.errors.positionUseRelease':
    'Freigegeben wird über die Ausführungsfreigabe, nicht über den Statuswechsel.',
  'projects.errors.positionReleaseNoOrder': 'Noch kein Auftrag: Freigeben geht erst ab „beauftragt".',
  'projects.errors.positionReasonRequired': 'Bitte eine Begründung angeben.',
  'projects.errors.positionBillingNotDone': 'Zuerst die Freigabe „Abrechnung fertig" setzen.',
  'projects.errors.positionInheritConflict':
    'Adresse entweder aus dem Partner wählen oder frei eingeben — nicht beides.',
  'projects.errors.supplementRevertLocked': 'Ein freigegebener Nachtrag bleibt ein Nachtrag.',
  'projects.errors.notASupplement': 'Diese Position ist kein Nachtrag.',
  'projects.errors.positionHasDependencies':
    'Diese Position hängt an Belegen, Stunden oder einer Leistungserklärung — stornieren statt löschen.',
  'projects.errors.positionReadonly': 'Diese Position ist abgerechnet oder storniert und bleibt, wie sie ist.',
  'projects.errors.groupNotFound': 'Diese Gruppe gibt es nicht mehr.',
  'projects.errors.clipboardEmpty': 'Die Zwischenablage ist leer.',
  'projects.errors.clipboardTenantMismatch': 'Dieser Eintrag gehört nicht zu diesem Mandanten.',
  'clipboard.errors.full': 'Die Zwischenablage ist voll — zuerst einfügen oder leeren.',
  'clipboard.errors.itemNotFound': 'Diesen Eintrag gibt es in der Zwischenablage nicht mehr.',

  'projects.errors.workStepNotFound': 'Diesen Arbeitsschritt gibt es nicht mehr.',
  // AP-35 Phase E — Brücke zur Zeiterfassung und Auftragsmappe (Blatt 4 6., Blatt 10)
  'projects.errors.mirrorNumberTaken':
    'Diese Nummer trägt schon ein Auftrag der Zeiterfassung. In den Einstellungen unter Zeiterfassung verknüpfen oder mit neuer Nummer übernehmen.',
  'projects.errors.timeBridgeProjectTimeMissing': 'Der Baustein Projektzeit der Zeiterfassung ist nicht gebucht.',
  'projects.errors.timeBridgeNotConnected': 'Die Zeiterfassung ist nicht angeschlossen.',
  'projects.errors.refNotFound': 'Diesen Auftrag der Zeiterfassung gibt es nicht mehr.',
  'projects.errors.refAlreadyLinked': 'Dieser Auftrag ist schon verknüpft.',
  'projects.errors.linkTargetHasBookings':
    'Auf dieses Ziel wurde schon gebucht — zwei Buchungsbestände lassen sich nicht per Knopf verschmelzen.',
  'projects.errors.folderNotAvailable': 'Für diese Position gibt es keine Mappe.',
  'projects.errors.photoOnly': 'Hier werden nur Fotos angenommen.',
  // AP-35 Phase G — Stückliste, Import, Einkauf (Blatt 5)
  'projects.errors.bomLineNotFound': 'Diese Stücklistenzeile gibt es nicht mehr.',
  'projects.errors.bomTrashNotFound': 'Diese gelöschte Zeile liegt nicht mehr im Papierkorb.',
  'projects.errors.bomTrashExists': 'Die Zeile ist schon wiederhergestellt.',
  // AP-35 Nachtrag 7 — Papierkorb der Positionen
  'projects.errors.positionTrashNotFound': 'Diese gelöschte Position liegt nicht mehr im Papierkorb.',
  'projects.errors.positionTrashExists': 'Die Position ist schon wiederhergestellt.',
  'projects.errors.positionTrashPhaseGone':
    'Ein Termin, an dem ihre Arbeitsschritte hingen, ist inzwischen gelöscht — die Position lässt sich so nicht wiederherstellen.',
  'projects.errors.positionTrashStepNumberTaken':
    'Die Nummern ihrer Arbeitsschritte sind inzwischen vergeben — die Position lässt sich so nicht wiederherstellen.',
  'projects.errors.consumableTrashNotFound': 'Diese gelöschte Zeile liegt nicht mehr im Papierkorb.',
  'projects.errors.consumableTrashExists': 'Die Zeile ist schon wiederhergestellt.',
  'projects.errors.bomQuantityInvalid': 'Die Menge muss größer 0 sein und zu den Nachkommastellen der Einheit passen.',
  'projects.errors.bomSupplierNotSupplier': 'Dieser Partner ist kein Lieferant.',
  'projects.errors.purchaseOrderOneSupplier': 'Eine Bestellung geht an genau einen Lieferanten.',
  'projects.errors.bomOrderedLocked': 'Die Zeile ist bestellt — Menge, Lieferant und Löschen erst nach dem Storno der Bestellung.',
  'projects.errors.bomCancelOrdered': 'Bestellte Zeilen werden über das Storno der Bestellung zurückgenommen.',
  'projects.errors.bomNotOrdered': 'Als erhalten lässt sich nur vermerken, was bestellt ist.',
  'projects.errors.bomApprovalLocked': 'Die Freigabe hängt schon an einem verbindlichen Beleg.',
  'projects.errors.bomCategoryExists': 'Diese Kategorie gibt es schon.',
  'projects.errors.bomCategoryInUse': 'Die Kategorie wird verwendet — deaktivieren statt löschen.',
  'projects.errors.bomCategoryNotFound': 'Diese Kategorie gibt es nicht mehr.',
  'projects.errors.importFileRequired': 'Bitte eine Datei wählen.',
  'projects.errors.importFileUnreadable': 'Die Datei ließ sich nicht lesen — CSV oder XLSX.',
  'projects.errors.importTooLarge': 'Die Datei hat mehr als 5.000 Zeilen — bitte teilen.',
  'projects.errors.importMappingInvalid': 'Die Spaltenzuordnung ist unvollständig.',
  'projects.errors.importMappingNotFound': 'Diese Zuordnung gibt es nicht mehr.',
  'projects.errors.importUndoLocked': 'Zeilen dieses Imports sind angefragt oder bestellt — der Import bleibt.',
  'projects.errors.importAlreadyUndone': 'Dieser Import ist schon zurückgenommen.',
  'projects.errors.importBatchNotFound': 'Diesen Import gibt es nicht mehr.',
  'documents.clipboard.pasteRejected':
    'Übernommene Zeilen passen nicht in diesen Beleg (Freigabe fehlt, anderer Lieferant, schon bestellt, geliefert oder abgerechnet). Es wurde nichts eingefügt.',
  // AP-35 N17-4: Übernahme aus einem Beleg (Preisanfrage → Bestellung) — die Zeilen und Gründe hängt `apiErrorText` an.
  'documents.transfer.rejected': 'Es wurde kein Beleg angelegt — diese Zeilen der Stückliste passen nicht:',
  // AP-35 Phase H — Verkauf und Abrechnung (Blatt 7)
  'projects.errors.salesLineNotFound': 'Diese Verkaufszeile gibt es nicht mehr.',
  'projects.errors.salesLineOneLevel': 'Unterpositionen haben keine eigenen Unterpositionen.',
  'projects.errors.salesLineLocked': 'Die Zeile ist geliefert oder abgerechnet — Korrektur über eine Gutschrift.',
  'projects.errors.salesLineDeliveredByDocument': 'Die Lieferung kommt aus einem Lieferschein und lässt sich nicht von Hand ändern.',
  'projects.errors.salesLineInDraft': 'Die Zeile steht in einem Belegentwurf — erst dort entfernen.',
  'projects.errors.salesLineSplitOpenOnly': 'Nur offene Zeilen ohne Unterpositionen und ohne Buchungen lassen sich teilen.',
  'projects.errors.salesLineSplitQuantity': 'Die Teilmenge muss zwischen 0 und der Menge der Zeile liegen.',
  'projects.errors.billingNotTimeAndMaterial': 'Die Position wird nicht nach Regie abgerechnet — bitte bestätigen, dass die Stunden als Nachtrag gelten.',
  'projects.errors.regieEntryBlocked': 'Eine Buchung ist schon abgerechnet oder steht in einem Regiebericht.',
  'projects.errors.regieEntryUnknown': 'Eine Buchung gehört nicht zu dieser Position.',
  'projects.errors.billingTabUnknown': 'Diesen Reiter gibt es nicht.',
  'projects.errors.tooManyIds': 'Zu viele Zeilen auf einmal — höchstens 100.',
  'projects.errors.partialInvoiceNoSource': 'Keine festgeschriebene Auftragsbestätigung — die Teilrechnung braucht eine Quelle.',
  'projects.errors.partialInvoicePercent': 'Der Stand muss über dem bereits abgerechneten Anteil und höchstens bei 100 % liegen.',
  'projects.errors.paymentPlanItemNotFound': 'Diese Rate gibt es nicht mehr.',
  'projects.errors.projectFromDocumentKind': 'Nur aus einem Angebot oder einer Auftragsbestätigung.',
  'projects.errors.projectFromDocumentLinked': 'Der Beleg ist schon verknüpft.',
  'projects.errors.articleNotFound': 'Diesen Artikel gibt es nicht mehr.',
  // AP-35 Phase I — Verbrauch und Nachkalkulation (Blatt 7 4., Blatt 8)
  'projects.errors.consumableNotFound': 'Diese Verbrauchszeile gibt es nicht mehr.',
  'projects.errors.consumableBilled': 'Der Verbrauch ist schon abgerechnet — erst die Verkaufszeile löschen.',
  'projects.errors.consumableNameRequired': 'Bitte eine Bezeichnung angeben.',
  'projects.errors.regieNothingSelected': 'Nichts gewählt — mindestens eine Buchung oder ein Verbrauch.',
  'projects.errors.regieConsumableUnknown': 'Ein Verbrauch gehört nicht zu dieser Position oder ist schon abgerechnet.',
  // AP-35 Phase F — Technik, Leistungserklärung, Materialzertifikate (Blatt 6, Blatt 5 7.)
  'projects.errors.techSchemaInvalid': 'Das Technik-Blatt lässt sich so nicht speichern.',
  'projects.errors.techTemplateNotFound': 'Diese Vorlage gibt es nicht.',
  'projects.errors.techValueInvalid': 'Ein Wert passt nicht zum Feld — bitte die Auswahl prüfen.',
  'projects.errors.declarationBlocked': 'Die Leistungserklärung kann nicht ausgestellt werden — siehe die roten Punkte der Prüfliste.',
  'projects.errors.declarationWithdrawReason': 'Bitte den Grund angeben.',
  'projects.errors.declarationNotIssued': 'Diese Erklärung ist schon zurückgezogen.',
  'projects.errors.declarationNotFound': 'Diese Leistungserklärung gibt es nicht.',
  'projects.errors.declarationAlreadyRendered': 'PDF und Etikett sind schon erzeugt.',
  'projects.errors.certificateFileRequired': 'Ein Zeugnis braucht die Datei.',
  'projects.errors.certificateIssuerRequired': 'Bitte den Aussteller angeben.',
  'projects.errors.certificateIssuerNotSupplier': 'Dieser Partner ist kein Lieferant.',
  'projects.errors.certificateNotFound': 'Dieses Zeugnis gibt es nicht mehr.',
  'projects.errors.certificateDeleteLinked': 'Das Zeugnis ist zugeordnet — deaktivieren statt löschen.',
  'projects.errors.settingValueInvalid': 'Die Herstellerdaten enthalten ein unbekanntes Feld.',
  // AP-35 Phase D2 — Cockpits und Einteilung (Blatt 11)
  'projects.errors.cockpitPhaseNotFound': 'Diese Phase gibt es nicht mehr oder sie ist abgeschaltet.',
  'projects.errors.cockpitGroupNotFound': 'Diese Gruppe gibt es nicht mehr.',
  'projects.errors.cockpitLayoutNotFound': 'Dieser Kollege hat hier noch keine Anordnung.',
  'projects.errors.assignmentNotFound': 'Diese Einteilung gibt es nicht mehr.',
  'projects.errors.assignRangeInvalid': 'Das Ende liegt vor dem Anfang.',
  'projects.errors.assignUserDuplicate': 'Jeder Mitarbeiter nur einmal je Einteilung.',
  'projects.errors.userNotFound': 'Diesen Mitarbeiter gibt es nicht mehr oder er ist deaktiviert.',
  'projects.errors.workStepPhase': 'Bitte eine Phase wählen.',
  'projects.errors.workStepHours': 'Soll-Stunden dürfen nicht negativ sein.',
  'projects.errors.workStepStatusTransition': 'Dieser Statuswechsel ist nicht vorgesehen.',
  'projects.errors.workStepUseRelease':
    'Freigegeben wird über die Freigabe des Schritts, nicht über den Statuswechsel.',
  'projects.errors.workStepReasonRequired': 'Bitte eine Begründung angeben.',
  'projects.errors.workStepDeleteLocked':
    'Auf diesen Schritt sind Stunden gebucht — stornieren statt löschen.',
  'projects.errors.workStepNumberImmutable': 'Die Nummer ist der Scan-Code und bleibt.',

  'projects.errors.journalNotFound': 'Diesen Eintrag gibt es nicht mehr.',
  'projects.errors.journalSystemType': 'Die Art „Ereignis" schreibt das System selbst.',
  'projects.errors.journalSystemImmutable': 'Systemeinträge lassen sich nicht ändern.',
  'projects.errors.journalDeleteLinked':
    'An diesem Eintrag hängt eine Aufgabe oder er ist angepinnt — zuerst lösen.',
  'projects.errors.journalFutureDate':
    'Ein Journal-Eintrag liegt nie in der Zukunft — dafür gibt es Aufgaben.',
  'projects.errors.pinSystemEntry':
    'Systemeinträge lassen sich nicht anpinnen — schreib eine Notiz mit Verweis.',
  'projects.errors.taskNotFound': 'Diese Aufgabe gibt es nicht mehr.',
  'projects.errors.taskDoneLocked': 'Erledigte Aufgaben werden nicht geändert — zuerst wieder öffnen.',

  'projects.errors.fileNotFound': 'Diese Datei gibt es nicht mehr.',
  'projects.errors.fileEmpty': 'Die Datei ist leer.',
  'projects.errors.fileTooLarge': 'Die Datei ist größer als erlaubt.',
  'projects.errors.fileLocked': 'Diese Datei gehört zu einem Zeugnis, einer Erklärung oder einem LV und bleibt.',
  // AP-35 Phase J: LV im Projekt (Detailblatt 12 8.)
  'projects.errors.lvFormatUnknown': 'Datei ist kein ONLV, B 2063 oder GAEB DA XML.',
  'projects.errors.lvVersionUnsupported':
    'Diese Fassung des Formats wird nicht unterstützt — bitte als ONLV ab 2009 oder GAEB DA XML 3.x exportieren.',
  'projects.errors.lvEncoding': 'Der Zeichensatz passt nicht — Umlaute prüfen und einen anderen wählen.',
  'projects.errors.lvEmpty': 'Die Datei enthält keine Positionen.',
  'projects.errors.lvBroken': 'Die Datei ließ sich nicht lesen.',
  'projects.errors.lvFileTooLarge': 'LV-Datei zu groß (höchstens 20 MB).',
  'projects.errors.lvNotFound': 'Dieses LV gibt es nicht (mehr).',
  'projects.errors.lvItemNotFound': 'Diese LV-Position gibt es nicht (mehr).',
  'projects.errors.lvItemTextOnly': 'Text ohne Menge — als Journal-Eintrag ablegen?',
  'projects.errors.lvDeleteNotCurrent': 'Gelöscht werden kann nur die aktuelle LV-Version.',
  'projects.errors.lvDeleteReferenced': 'Verkaufszeilen verweisen auf dieses LV — erst die Zeilen lösen oder löschen.',
  'projects.errors.lvPasteNeedsBilling': 'Zusammenfassen und „in Position“ brauchen Verkaufszeilen (Baustein Abrechnung).',
  'projects.errors.lvChangeNone': 'Diese Verkaufszeile hat keine LV-Änderung.',
  'projects.errors.lvChangeLineNotOpen': 'Die Zeile ist nicht mehr offen — bitte eine Differenzzeile anlegen.',
  'projects.errors.lvItemRemoved': 'Die Position ist im LV entfallen — nichts zu übernehmen.',
  'projects.errors.unitNotFound': 'Diese Einheit gibt es nicht.',
  'projects.errors.fileEntityUnsupported':
    'An diesem Gegenstand können noch keine Dateien hängen — der Baustein fehlt.',

  // AP-30: Rollen und Rechte
  'roles.notFound': 'Diese Rolle gibt es nicht mehr.',
  'roles.unknownRole': 'Diese Rolle gibt es bei diesem Betrieb nicht.',
  'roles.unknownTemplate': 'Diese Systemvorlage gibt es nicht.',
  'roles.keyExists': 'Es gibt schon eine Rolle mit diesem Schlüssel. Wähle einen anderen.',
  'roles.keyInvalid':
    'Der Schlüssel besteht aus Kleinbuchstaben, Ziffern und Unterstrichen — z. B. lieferscheine_ohne_preise.',
  'roles.userNotFound': 'Diesen Benutzer gibt es nicht mehr.',
  'roles.systemRoleReadonly':
    'Eine Systemrolle wird nicht bearbeitet, sondern kopiert. Die Kopie lässt sich beliebig anpassen.',
  'roles.systemRoleNotDeletable':
    'Systemrollen sind nicht löschbar — sie sind der Einstieg für jeden neuen Betrieb.',
  'roles.lastRoleManager':
    'Das ist der letzte Zugang dieses Betriebs, der Rollen verwalten darf. ' +
    'Vergib das Recht zuerst an jemand anderen.',

  // Dieselben Fälle bauen `rechteSperreText` unten aus `code` + Nutzlast zu
  // einem genaueren Satz. Diese beiden Zeilen greifen, wenn eine Aufrufstelle
  // nur den Schlüssel hat — besser ein knapper Satz als ein Schlüssel.
  'permissions.denied': 'Dafür fehlt dir ein Recht.',
  'permissions.missingDeclaration':
    'Dieser Aufruf ist im Programm nicht freigegeben. Bitte dem Support melden.',
}

function looksLikeKey(msg: string): boolean {
  return /^[a-zA-Z]+(\.[a-zA-Z0-9]+)+$/.test(msg)
}

/**
 * Übersetzt einen bekannten Fehler-Key oder gibt Klartext/`fallback` zurück.
 *
 * **Zweite Quelle seit AP-36 Phase E:** Viele Schlüssel des Servers stehen
 * bereits in den Übersetzungsdateien der Oberfläche (`articles.*`, `clients.*`).
 * Sie hier ein zweites Mal zu pflegen hieße, zwei Texte auseinanderlaufen zu
 * lassen — deshalb fragt die Funktion i18n, bevor sie aufgibt. Am Feld steht so
 * der Satz des Servers und nicht „Das hat nicht geklappt.".
 */
export function translateApiErrorKey(msg: unknown, fallback: string): string {
  if (typeof msg !== 'string' || msg.length === 0) return fallback
  if (API_ERROR_MESSAGES[msg]) return API_ERROR_MESSAGES[msg]
  if (looksLikeKey(msg)) {
    const uebersetzt = i18n.exists(msg) ? i18n.t(msg) : null
    // Ein Schlüssel, der auf sich selbst zeigt, ist keine Übersetzung.
    return uebersetzt && uebersetzt !== msg ? uebersetzt : fallback
  }
  return msg
}

/**
 * AP-29-N3 — Die Modulsperre als lesbarer Satz.
 *
 * Backend-seitig ist `MODULE_NOT_ACTIVE` ein Schlüssel samt Modulkennung
 * (`common/module-access-check.ts`, `common/module-fields.ts`). Das ist für ein
 * Protokoll richtig und für eine Meldung am Bildschirm unbrauchbar — genau der
 * Satz, den der Auftraggeber am 12.9.2026 beim Einstempeln zu sehen bekam.
 *
 * Nach diesem Nachtrag soll dieser Fall in der Oberfläche nicht mehr auftreten;
 * über die Schnittstelle kann er es weiterhin. Dann trägt er wenigstens den
 * **Bausteinnamen** und den Weg zur Buchung.
 */
function moduleSperreText(daten: Record<string, unknown>): string | null {
  if (daten.code !== 'MODULE_NOT_ACTIVE') return null

  const schluessel = typeof daten.module === 'string' ? daten.module : null
  const name = schluessel ? moduleLabel(schluessel, i18n.t.bind(i18n)) : null

  return name
    ? `„${name}" ist für diesen Betrieb nicht gebucht. Unter „Einstellungen → Module" steht, was aktiv ist und wie sich das ändern lässt.`
    : 'Dieser Teil ist für diesen Betrieb nicht gebucht. Unter „Einstellungen → Module" steht, was aktiv ist.'
}

/** Die Bezeichnung eines Rechts, nicht sein Schlüssel. */
function rechtName(p: unknown): string {
  if (typeof p !== 'string') return ''
  // AP-35: Rechte des Projektmanagements tragen den Begriff des Mandanten.
  const projectPlural = i18n.t(`terminology.${getTerminologyMode()}.projectPlural`)
  return i18n.t(`permissions.${p}`, { defaultValue: p, projectPlural })
}

function rechteListe(daten: Record<string, unknown>, max = 5): string {
  const alle = Array.isArray(daten.permissions) ? daten.permissions : []
  const namen = alle.slice(0, max).map(rechtName).filter(Boolean)
  const rest = alle.length - namen.length
  return namen.join(', ') + (rest > 0 ? ` (und ${rest} weitere)` : '')
}

/**
 * AP-30 — Rechtefehler als Satz.
 *
 * Das Backend nennt den Fall (`code`) und die betroffenen Rechte
 * (`permission`/`permissions`); den Satz baut die Oberfläche. Der Grund ist
 * derselbe wie bei `MODULE_NOT_ACTIVE`: Ein serverseitig zusammengesetzter Satz
 * ist in keiner zweiten Sprache übersetzbar, und ein Rechte-**Schlüssel** am
 * Bildschirm ist für den Kunden keine Auskunft.
 */
function rechteSperreText(daten: Record<string, unknown>): string | null {
  switch (daten.code) {
    case 'PERMISSION_DENIED': {
      const name = rechtName(daten.permission)
      return name
        ? `Dafür fehlt dir das Recht „${name}". Ein Administrator kann es in der Rollenverwaltung vergeben.`
        : 'Dafür fehlt dir ein Recht. Ein Administrator kann es in der Rollenverwaltung vergeben.'
    }
    case 'PERMISSION_ESCALATION': {
      const liste = rechteListe(daten)
      return liste
        ? `Du kannst nur Rechte vergeben, die du selbst hast. Dir fehlen: ${liste}.`
        : 'Du kannst nur Rechte vergeben, die du selbst hast.'
    }
    case 'UNKNOWN_PERMISSIONS': {
      const liste = Array.isArray(daten.permissions) ? daten.permissions.join(', ') : ''
      return liste
        ? `Diese Rechte kennt der Katalog nicht: ${liste}.`
        : 'Diese Rechte kennt der Katalog nicht.'
    }
    case 'PERMISSION_MISSING_DECLARATION':
      // Kein Kundenfehler, sondern ein vergessener Dekorator. Der Satz sagt
      // das, statt den Kunden nach seinen Rechten suchen zu lassen.
      return 'Dieser Aufruf ist im Programm nicht freigegeben (fehlende Rechte-Angabe). Bitte dem Support melden.'
    default:
      return null
  }
}

/** Die Gründe, aus denen ein Modul der Herkunft eine Zeile zurückweist (Stückliste, AP-35 Blatt 5 5.2). */
const ZEILEN_GRUND: Record<string, (detail?: string) => string> = {
  BOM_APPROVAL_REQUIRED: (d) => (d === 'INQUIRY' ? 'Anfragefreigabe fehlt' : 'Bestellfreigabe fehlt'),
  BOM_ALREADY_ORDERED: (d) => (d ? `schon bestellt (${d})` : 'schon bestellt'),
  BOM_SUPPLIER_MISMATCH: (d) => (d ? `gehört zu Lieferant ${d}` : 'gehört zu einem anderen Lieferanten'),
}

/**
 * AP-35 N17-4 — Einfügen bzw. Übernehmen abgelehnt: hinter den Satz kommt je Zeile, warum
 * („Träger HEA 200: Bestellfreigabe fehlt · Flachstahl 60×8: schon bestellt (B2026-0012)").
 */
function abgelehnteZeilen(daten: Record<string, unknown>): string | null {
  if ((daten.code !== 'TRANSFER_REJECTED' && daten.code !== 'PASTE_REJECTED') || !Array.isArray(daten.errors)) return null
  const zeilen = (daten.errors as { code?: string; label?: string; detail?: string }[]).map((f) => {
    const grund = (f.code && ZEILEN_GRUND[f.code]?.(f.detail)) || 'passt nicht'
    return f.label ? `${f.label}: ${grund}` : grund
  })
  return zeilen.length ? zeilen.join(' · ') : null
}

/** Liefert eine anzeigbare Fehlermeldung direkt aus einem Axios-Fehler. */
export function apiErrorText(err: unknown, fallback: string): string {
  const daten = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
  if (daten) {
    const sperre = moduleSperreText(daten) ?? rechteSperreText(daten)
    if (sperre) return sperre
    const zeilen = abgelehnteZeilen(daten)
    if (zeilen) return `${translateApiErrorKey(daten.message, fallback)} ${zeilen}`
  }
  return translateApiErrorKey(daten?.message, fallback)
}
